import { HTMLTemplateElement, isInstanceOf } from "@acng/frontend-bounty/types";
import { isCommentNode, isElementNode, isTextNode } from "@acng/frontend-bounty/dom/type.js";
import { before, createText, hasDataAttribute, removeNode, whenAll } from "@acng/frontend-bounty";
import { pushToArray } from "@acng/frontend-bounty/array";
import { parseJSON } from "@acng/frontend-bounty/parse";
import { STRING, typeguard } from "@acng/frontend-bounty/typeguard.js";
import { setupCustomElement } from "./feature.js";
import { RESOURCE_COMPILE } from "../model/resource.js";
import { replace } from "./replace.js";
import { isInjectable } from "./angular.js";
const REGEXP = /([#$]){([^}]*)}#?({)?/g;
const REGEXP_JSON = /({.*?})#/g;
//const REGEXP_DIR = /#{([^}]*)}/g;
const REGEXP_ID = /(\w[\w-]*)/g;
/**
 * @typedef {import('../model/resource.js').ResourceElement} ResourceElement
 */
/**
 * @param {ResourceElement} resource
 */
export const compileOnDemand = async (resource) => {
    if (!resource[RESOURCE_COMPILE]) {
        DEBUG: if (hasDataAttribute(resource, "debug")) {
            performance.mark("stargazer-compile-started");
            console.debug("compile resource", { resource });
        }
        /** @type {Promise<any>[]} */
        const promises = [];
        compileChildNodes(resource, promises, resource.content);
        resource[RESOURCE_COMPILE] = whenAll(promises);
        DEBUG: if (hasDataAttribute(resource, "debug")) {
            resource[RESOURCE_COMPILE].then((res) => {
                console.debug("compile resource", {
                    results: res,
                    duration: performance.measure("stargazer-compile", "stargazer-compile-started").duration,
                });
            });
        }
    }
    await resource[RESOURCE_COMPILE];
};
/**
 * @param {import("../model/resource.js").ResourceElement} resource
 * @param {Promise<any>[]} promises
 * @param {Node} node
 * @ŧodo TODO detect and break loops
 */
const compileChildNodes = (resource, promises, node) => {
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const childNode = node.childNodes[i];
        if (isTextNode(childNode)) {
            compileTextNode(resource, childNode, promises);
        }
        else if (isInstanceOf(childNode, HTMLTemplateElement)) {
            pushToArray(promises, compileOnDemand(childNode));
        }
        else if (isElementNode(childNode)) {
            compileElement(resource, childNode, promises);
        }
        else if (isCommentNode(childNode)) {
            removeNode(childNode);
        }
    }
};
/**
 * @param {import("../model/resource.js").ResourceElement} resource
 * @param {Text} node
 * @param {Promise<any>[]} promises
 */
const compileTextNode = (resource, node, promises) => {
    const text = node.nodeValue;
    // Typescript thinks that nodeValue of Text might be null.
    ASSERT: typeguard("Text.nodeValue", text, STRING);
    /** @type {RegExpExecArray | null} */
    let match;
    let lastMatch = 0;
    REGEXP.lastIndex = 0;
    while ((match = REGEXP.exec(text))) {
        const [, prefix, dir, suffix] = match;
        /** @type {Record<string, string> | undefined} */
        let params;
        if (match.index > lastMatch) {
            before(node, text.substring(lastMatch, match.index));
        }
        if (suffix) {
            REGEXP_JSON.lastIndex = REGEXP.lastIndex - 1;
            match = REGEXP_JSON.exec(text);
            if (match) {
                params = parseParameters(match[1]);
                REGEXP.lastIndex = REGEXP_JSON.lastIndex;
            }
        }
        lastMatch = REGEXP.lastIndex;
        if (prefix != "#") {
            const variable = parseValue(dir);
            if (variable) {
                // resource[RESOURCE_TEXT] = true; TODO maybe add data-attribute to resource
                before(node, variable);
            }
            continue;
        }
        const asyncReplace = createText(dir);
        const path = parseDirective(dir);
        before(node, asyncReplace);
        pushToArray(promises, replace(resource, asyncReplace, path, params));
        REGEXP.lastIndex = lastMatch;
    }
    if (lastMatch < text.length) {
        before(node, text.substring(lastMatch));
    }
    removeNode(node);
};
/**
 * @param {string} json
 * @returns {Record<string, string> | undefined}
 */
const parseParameters = (json) => {
    try {
        return parseJSON(json);
    }
    catch (reason) {
        DEBUG: console.error("ignoring parameters with syntax error", { reason, json });
    }
};
/**
 * @param {string} data
 */
const parseValue = (data) => {
    REGEXP_ID.lastIndex = 0;
    const match = REGEXP_ID.exec(data);
    if (match) {
        return `\${${match[1]}}`;
    }
};
//const REGEXP_UP = /\.(\.)?\//g;
/**
 * @param {string} data
 *
 * TODO add base param to allow relative directive. add origin-url as symbol property to each template?
 * TODO detect loader type and rearrange to path array
 */
const parseDirective = (data) => {
    /** @type {RegExpExecArray | null} */
    let match;
    REGEXP_ID.lastIndex = 0;
    /*
    REGEXP_UP.lastIndex = 0;
    while ((match = REGEXP_UP.exec(data))) {
      if (match[1]) {
        dir = sliceOfArray(dir, 0, -1);
      }
      REGEXP_ID.lastIndex = REGEXP_UP.lastIndex;
    }
    if (REGEXP_ID.lastIndex == 0) {
      dir = [];
    }
    */
    /**
     * @type {string[]}
     */
    const dir = [];
    while ((match = REGEXP_ID.exec(data))) {
        pushToArray(dir, match[1]);
    }
    return dir;
};
/**
 * @param {ResourceElement} resource
 * @param {Element} element
 * @param {Promise<any>[]} promises
 */
const compileElement = (resource, element, promises) => {
    // TODO make legacy mode obsolete TODO //
    for (const { name } of element.attributes) {
        if (/^onsw-/.test(name) && !isInjectable(name)) {
            //DEBUG: console.warn(`${MODULE} attibute failed`, {name});
            removeNode(element);
            return;
        }
        /*if (/^on:/.test(name)) { TODO may be add data-attribute to resource
          resource[RESOURCE_EVENT] = true;
        }*/
    }
    if (!setupCustomElement(element.localName, promises)) {
        removeNode(element);
    }
    else {
        compileChildNodes(resource, promises, element);
    }
};
